import {
  createStyleVariantType,
  getStyleVariantTypeArguments,
} from '@packages/systems/component-style-variants';
import {
  Types,
  Type,
  getSystem,
  withSystem,
  Component,
} from '@packages/systems/wfdl';
import {
  MutableQualifiedMap,
  Qualified,
  getName,
  getPluginName,
  qualify,
} from '@packages/systems/wfdl-Qualified';

const getComponentNameFromOriginMap = (
  currentComponentName: string,
  originType: Qualified<string>,
  componentOriginMap: MutableQualifiedMap<
    Component.ComponentName,
    Qualified<Component.ComponentName>
  >
): string | undefined => {
  const componentName = componentOriginMap.get(
    qualify(getPluginName(originType), currentComponentName)
  );
  return componentName ? getName(componentName) : undefined;
};

/**
 * Updates the Style Variant type to contain the correct plugin name and component name.
 * PluginName is supplied as a parameter, while the component name is derived from the currentComponentName that is in the StyleVariant dataType
 * along with originType and componentOriginMap which are supplied as parameters. We lookup the component name from the componentOriginMap
 * based on originType and currentComponentName.
 */
const updateStyleVariantType = (
  dataType: Type,
  fieldId: string,
  pluginName: string,
  originType?: Qualified<string>,
  componentOriginMap?: MutableQualifiedMap<
    Component.ComponentName,
    Qualified<Component.ComponentName>
  >
): {result: Type; hasMissingStyleVariants: boolean} => {
  let hasMissingStyleVariants = false;
  const result = Types.updateRecordField(dataType, fieldId, (type) => {
    const {
      variantFieldId,
      componentName: currentComponentName,
      pluginName: _,
    } = getStyleVariantTypeArguments(type);
    if (!currentComponentName) {
      return type;
    }
    // If optional originType and componentOriginMap are provided, we derive the componentName from the componentOriginMap
    // by using the currentComponentName from the dataType and originType parameter
    const componentName =
      originType && componentOriginMap
        ? getComponentNameFromOriginMap(
            currentComponentName,
            originType,
            componentOriginMap
          )
        : currentComponentName;
    if (!variantFieldId || !componentName) {
      hasMissingStyleVariants = true;
      return type;
    }
    const updatedType = createStyleVariantType(
      pluginName,
      componentName,
      variantFieldId
    );
    return withSystem(getSystem(type) || {}, updatedType);
  });
  return {
    result,
    hasMissingStyleVariants,
  };
};

export const updateStyleVariants = (
  dataType: Type,
  pluginName: string,
  originType?: Qualified<string>,
  componentOriginMap?: MutableQualifiedMap<
    Component.ComponentName,
    Qualified<Component.ComponentName>
  >
): {updatedDataType: Type; hasMissingStyleVariants: boolean} => {
  const fields = Types.getRecordFields(dataType);
  let hasMissingStyleVariants = false;
  if (!fields) {
    return {updatedDataType: dataType, hasMissingStyleVariants: false};
  }
  const updatedDataType = Object.entries(fields).reduce(
    (acc, [fieldId, field]) => {
      if (Types.isStyleVariantType(field)) {
        const {result, hasMissingStyleVariants: missingVariants} =
          updateStyleVariantType(
            acc,
            fieldId,
            pluginName,
            originType,
            componentOriginMap
          );
        hasMissingStyleVariants = hasMissingStyleVariants || missingVariants;
        return result;
      }
      return acc;
    },
    dataType
  );
  return {updatedDataType, hasMissingStyleVariants};
};
